import { useCallback, useState } from 'react';

import { Form, Input } from 'antd';
import type { InputProps } from 'antd/es/input';

import { ClearIcon } from '@/shared/assets/icons/input/ClearIcon';
import { COLOR } from '@/shared/design-tokens/design-tokens';
import { Icon } from '@/shared/ui/Icon';

import styles from './TextInput.module.css';

type TextInputProps = Omit<InputProps, 'allowClear'> & {
  allowClear?: boolean | { clearIcon?: React.ReactNode };
};

const CLEAR_ICON_SIZE = 16;
const SUFFIX_ICON_SIZE = 16;
const DEFAULT_CLEAR_ICON_BG = COLOR.GRAY[70];
const DEFAULT_CLEAR_ICON_COLOR = COLOR.GRAY[100];

const DEFAULT_CLEAR_CONFIG = {
  clearIcon: (
    <ClearIcon size={CLEAR_ICON_SIZE} backgroundColor={DEFAULT_CLEAR_ICON_BG} iconColor={DEFAULT_CLEAR_ICON_COLOR} />
  ),
};

function TextInput({ className, allowClear = true, type, 'aria-label': ariaLabel, ...restProps }: TextInputProps) {
  const { status } = Form.Item.useStatus();

  const mergedClassName = className ? `${styles.input} ${className}` : styles.input;

  const allowClearConfig =
    allowClear === false
      ? false
      : typeof allowClear === 'object' && allowClear.clearIcon
        ? allowClear
        : DEFAULT_CLEAR_CONFIG;

  const isPassword = type === 'password';
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = useCallback(() => {
    setShowPassword((prev) => !prev);
  }, []);

  const resolvedType = isPassword && showPassword ? 'text' : type;

  const suffix = (
    <>
      {status === 'error' && <Icon className={styles.errorIcon} name="error" size={SUFFIX_ICON_SIZE} />}
      {isPassword && (
        <Icon
          name={showPassword ? 'eye-on' : 'eye-off'}
          size={SUFFIX_ICON_SIZE}
          onClick={togglePasswordVisibility}
          className={styles.passwordToggle}
          color={COLOR.GRAY[60]}
        />
      )}
    </>
  );

  return (
    <Input
      className={mergedClassName}
      allowClear={allowClearConfig}
      type={resolvedType}
      suffix={suffix}
      aria-label={ariaLabel}
      {...restProps}
    />
  );
}

export { TextInput };
